// src/modules/competitors/pages/CompetitorsOverview/index.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Form } from 'react-bootstrap';
import { useService } from '../../../../shared/hooks/useService';
import Loading from '../../../../shared/components/Loading';
import { useWarmupTimer } from '../../../../shared/hooks/useWarmupTimer';
import ErrorState from '../../../../shared/components/ErrorState';
import CompetitorCard from '../../components/CompetitorCard';
import './styles.css';

const PAGE_SIZE = 12;

const CompetitorsOverview = () => {
    const [competitors, setCompetitors] = useState([]);
    const [filteredCompetitors, setFilteredCompetitors] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({ activeInCompetition: false, stravaConnected: false, notInCompetition: false });
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [hasMore, setHasMore] = useState(false);
    const { isWarmup } = useWarmupTimer(isLoading);
    const [error, setError] = useState(null);
    const service = useService();
    const loadMoreRef = useRef(null);

    // Refs for stable access inside callbacks
    const nextTokenRef = useRef(null);
    const isLoadingMoreRef = useRef(false);
    const abortRef = useRef(0); // Incremented on each reset to cancel stale fetches

    const fetchPage = useCallback(async (append, token, abortId, currentFilters) => {
        try {
            const result = await service.getAllUsers({
                pageSize: PAGE_SIZE,
                nextToken: token,
                filters: currentFilters
            });

            // Discard result if a newer fetch has started
            if (abortRef.current !== abortId) return;

            if (append) {
                setCompetitors(prev => [...prev, ...result.users]);
            } else {
                setCompetitors(result.users);
            }

            nextTokenRef.current = result.nextToken || null;
            setHasMore(!!result.nextToken);
        } catch (err) {
            if (abortRef.current !== abortId) return;
            setError(err.message);
        } finally {
            if (abortRef.current !== abortId) return;
            setIsLoading(false);
            isLoadingMoreRef.current = false;
            setIsLoadingMore(false);
        }
    }, [service]);

    // Reset and load first page whenever filters change
    useEffect(() => {
        const abortId = ++abortRef.current;
        nextTokenRef.current = null;
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
        setHasMore(false);
        setIsLoading(true);
        setError(null);
        fetchPage(false, null, abortId, filters);
    }, [filters, fetchPage]);

    // IntersectionObserver — only active after initial load and when more pages exist
    useEffect(() => {
        if (isLoading) return;

        const observer = new IntersectionObserver(
            entries => {
                if (
                    entries[0].isIntersecting &&
                    nextTokenRef.current &&
                    !isLoadingMoreRef.current
                ) {
                    const abortId = abortRef.current; // Same fetch generation — no reset
                    isLoadingMoreRef.current = true;
                    setIsLoadingMore(true);
                    fetchPage(true, nextTokenRef.current, abortId, filters);
                }
            },
            { rootMargin: '200px' }
        );

        const currentRef = loadMoreRef.current;
        if (currentRef) observer.observe(currentRef);

        return () => observer.disconnect();
    }, [isLoading, fetchPage, filters]);

    // Client-side search on the loaded competitors
    useEffect(() => {
        let result = [...competitors];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase().trim();
            result = result.filter(c => {
                const fullName = `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase();
                const username = (c.username || '').toLowerCase();
                return fullName.includes(term) || username.includes(term);
            });
        }

        setFilteredCompetitors(result);
    }, [searchTerm, competitors]);

    const toggleFilter = (key) => {
        setFilters(prev => ({ ...prev, [key]: !prev[key] }));
    };

    if (isLoading) return <Loading isWarmup={isWarmup} />;
    if (error) return <ErrorState message={error} onRetry={() => setFilters(f => ({ ...f }))} />;

    return (
        <div className="competitors-page">
            <div className="competitors-header">
                <Container>
                    <div className="header-content">
                        <div className="header-title">
                            <h1>Competitors</h1>
                             <p className="header-subtitle">
                                 See who's competing
                             </p>
                        </div>
                    </div>
                </Container>
            </div>

            <Container>
                <div className="controls-bar">
                    <Form.Control
                        type="text"
                        placeholder="Search by name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

                <div className="filters-bar">
                    <button
                        className={`filter-chip ${filters.activeInCompetition ? 'active' : ''}`}
                        onClick={() => toggleFilter('activeInCompetition')}
                    >
                        In Active Competition
                    </button>
                    <button
                        className={`filter-chip ${filters.stravaConnected ? 'active' : ''}`}
                        onClick={() => toggleFilter('stravaConnected')}
                    >
                        Strava Connected
                    </button>
                    <button
                        className={`filter-chip ${filters.notInCompetition ? 'active' : ''}`}
                        onClick={() => toggleFilter('notInCompetition')}
                    >
                        Not in Active Competition
                    </button>
                </div>

                {filteredCompetitors.length > 0 ? (
                    <div className="competitors-grid">
                        {filteredCompetitors.map(competitor => (
                            <CompetitorCard
                                key={competitor.username}
                                competitor={competitor}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="empty-state">
                        <div className="empty-state-icon">👥</div>
                        <h3>No competitors found</h3>
                        <p>
                            {searchTerm ? 'Try a different search term.' : 'No competitors match your filters.'}
                        </p>
                        {searchTerm && (
                            <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
                                Clear search
                            </button>
                        )}
                    </div>
                )}
                <div className="load-more-container" ref={loadMoreRef}>
                    {isLoadingMore && (
                        <div className="loading-indicator">
                            Loading more competitors...
                        </div>
                    )}
                </div>
            </Container>
        </div>
    );
};

export default CompetitorsOverview;
